import * as React from 'react';
import * as styles from './SimpleDropDownDatePicker.scss';
import { ActionButton, IButton, IButtonStyles } from '@fluentui/react/lib/Button';
import { Callout } from '@fluentui/react/lib/Callout';
import { ControlIcons } from 'owa-control-icons';
import { Icon, IIconProps } from '@fluentui/react/lib/Icon';
import { InlineDatePicker } from 'owa-datepicker';
import { observer } from 'mobx-react-lite';
import { OwaDate, formatMonthDayYear } from 'owa-datetime';
import { useToggleState } from 'owa-react-hooks/lib/useToggleState';
import classnames from 'classnames';

const removeDateIcon: IIconProps = {
    iconName: ControlIcons.Clear,
    styles: { root: { fontSize: 12 } },
};

/**
 * A simple drop-down date picker, designed to use a small screen footprint.
 * It consists of a button to show the date and invoke the picker, and an optional button to remove the date.
 * `chooseDateText` is shown if the date is `null`, otherwise, the formatted date is shown.
 * By default, a chevron shows up next to a selected date. Use `showChevron` to always show or never show the chevron.
 * The remove button is shown if _any_ of the `removeDate*` text properties are given.
 * If `removeDateText` is given, the text shows up, otherwise we show an X icon.
 * For an even smaller footprint, use { font-size: ms-font-size-s; height: 20px } in `buttonsClassName`.
 */
export default observer(function SimpleDropDownDatePicker({
    value,
    onDateSelected,
    timeZoneId,
    minDate,
    formatDate = formatMonthDayYear,
    chooseDateStyles,
    chooseDateText,
    chooseDateAriaLabel,
    chooseDateTitle,
    removeDateStyles,
    removeDateText,
    removeDateAriaLabel,
    removeDateTitle,
    showChevron,
    className,
    buttonsClassName,
    iconClassName,
    onCalloutVisibilityChange,
    children,
}: {
    value: OwaDate | null | undefined;
    onDateSelected: (date: OwaDate | null) => void;
    timeZoneId?: string;
    minDate?: OwaDate;
    formatDate?: (date: OwaDate) => string;
    chooseDateStyles?: IButtonStyles;
    chooseDateText: string;
    chooseDateAriaLabel?: string;
    chooseDateTitle?: string;
    removeDateStyles?: IButtonStyles;
    removeDateText?: string;
    removeDateAriaLabel?: string;
    removeDateTitle?: string;
    showChevron?: boolean;
    className?: string;
    buttonsClassName?: string;
    iconClassName?: string;
    onCalloutVisibilityChange?: (calloutIsVisible: boolean) => void;
    children?: JSX.Element | JSX.Element[];
}) {
    const calloutTargetRef = React.useRef<HTMLDivElement>(null);
    const chooseDateRef = React.useRef<IButton>(null);

    const hasDateValue = !!value;
    const chooseDateEffectiveText = hasDateValue ? formatDate(value!) : chooseDateText;
    const chevronIsVisible = showChevron === undefined ? hasDateValue : showChevron;
    const hasAnyRemoveString = !!removeDateText || !!removeDateAriaLabel || !!removeDateTitle;
    const removeDateIsVisible = hasDateValue && hasAnyRemoveString;
    const removeDateIconProps = !removeDateText ? removeDateIcon : undefined;

    const rootClassName = classnames(styles.simpleDropDownDatePicker, className!);
    const chooseDateClassName = classnames(styles.showPickerButton, buttonsClassName!);
    const removeDateClassName = classnames(styles.button, buttonsClassName!);

    const [calloutIsVisible, toggleCallout, , dismissCallout] = useToggleState(false);

    const reportDateAndClosePicker = React.useCallback(
        (date: OwaDate | null) => {
            onDateSelected(date);
            dismissCallout();
            chooseDateRef.current!.focus();
        },
        [onDateSelected, dismissCallout, chooseDateRef]
    );

    const removeDate = React.useCallback(() => {
        reportDateAndClosePicker(null);
    }, [reportDateAndClosePicker]);

    React.useEffect(() => {
        if (onCalloutVisibilityChange) {
            onCalloutVisibilityChange(calloutIsVisible);
        }
    }, [onCalloutVisibilityChange, calloutIsVisible]);

    // NOTE: the text property on the ActionButton adds some extra padding we don't want.
    //       We pass the text as a child element to prevent that extra padding.
    return (
        <div ref={calloutTargetRef} className={rootClassName}>
            {iconClassName && <Icon className={iconClassName} iconName={ControlIcons.Clock} />}
            <ActionButton
                componentRef={chooseDateRef}
                className={chooseDateClassName}
                styles={chooseDateStyles}
                onClick={toggleCallout}
                ariaLabel={chooseDateAriaLabel}
                title={chooseDateEffectiveText ?? chooseDateTitle}>
                <div className={styles.chooseDateText}>{chooseDateEffectiveText}</div>
                {chevronIsVisible && (
                    <Icon className={styles.chevron} iconName={ControlIcons.ChevronDown} />
                )}
            </ActionButton>
            {removeDateIsVisible && (
                <ActionButton
                    className={removeDateClassName}
                    styles={removeDateStyles}
                    onClick={removeDate}
                    ariaLabel={removeDateAriaLabel}
                    title={removeDateTitle}
                    iconProps={removeDateIconProps}>
                    {removeDateText}
                </ActionButton>
            )}
            {calloutIsVisible && (
                <Callout
                    target={calloutTargetRef}
                    isBeakVisible={false}
                    setInitialFocus={true}
                    onDismiss={dismissCallout}>
                    <InlineDatePicker
                        timeZoneId={timeZoneId}
                        minDate={minDate}
                        value={value}
                        onSelectDate={reportDateAndClosePicker}
                    />
                    {children}
                </Callout>
            )}
        </div>
    );
});
